/**
 * ShiftMedicationTask — carer-facing medication administration view
 *
 * Appears on the worker dashboard during/near a shift that has a service user assigned.
 * Provides large, clear action buttons for recording administration: Given / Refused / Not Required.
 * Competency-gated — locked with explanation if competency not passed.
 *
 * Legal basis: Health & Social Care Act 2008 Reg 12(2)(g), NICE SC1
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Pill, CheckCircle2, XCircle, MinusCircle, AlertTriangle, Clock,
  Shield, Info, Lock, ChevronLeft, RefreshCw, Plane, Building2,
  AlertCircle, CheckCheck,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

// ─── STATUS ACTIONS ──────────────────────────────────────────────────────────
const QUICK_ACTIONS = [
  {
    status: 'given',
    label: 'Given',
    desc: 'Administered as prescribed',
    icon: CheckCircle2,
    bg: 'bg-[#1F8F5F]',
    hover: 'hover:bg-[#18785200]',
    activeBg: 'bg-[#1F8F5F]',
    text: 'text-white',
    border: 'border-transparent',
  },
  {
    status: 'refused',
    label: 'Refused',
    desc: 'Service user declined — reason required',
    icon: XCircle,
    bg: 'bg-red-600',
    hover: 'hover:bg-red-700',
    activeBg: 'bg-red-600',
    text: 'text-white',
    border: 'border-transparent',
  },
  {
    status: 'not_required',
    label: 'Not Required',
    desc: 'Not needed at this time',
    icon: MinusCircle,
    bg: 'bg-slate-500',
    hover: 'hover:bg-slate-600',
    activeBg: 'bg-slate-500',
    text: 'text-white',
    border: 'border-transparent',
  },
];

const OTHER_STATUSES = [
  { status: 'away',              label: 'Away',             icon: Plane       },
  { status: 'hospital',         label: 'Hospital',         icon: Building2   },
  { status: 'not_available',    label: 'Out of stock',     icon: AlertTriangle },
  { status: 'self_administered',label: 'Self-admin',        icon: Shield      },
  { status: 'carer_unavailable',label: 'Carer unavailable', icon: AlertCircle },
];

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr = parseInt(h, 10);
  return `${hr === 0 ? 12 : hr > 12 ? hr - 12 : hr}:${m} ${hr < 12 ? 'AM' : 'PM'}`;
};

// ─── RECORD DIALOG ────────────────────────────────────────────────────────────
function RecordDialog({ open, med, onClose, onSaved, serviceUserId, shiftId }) {
  const [status, setStatus] = useState('given');
  const [actualTime, setActualTime] = useState('');
  const [doseGiven, setDoseGiven] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !med) return;
    const now = new Date();
    setActualTime(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
    setDoseGiven(med.dose || '');
    setStatus(med.is_controlled_drug ? 'refused' : 'given');
    setReason('');
  }, [open, med]);

  if (!open || !med) return null;

  const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
  const cdEntry = med?.controlled_drug_entry;
  const cdReady = !med?.is_controlled_drug || cdEntry?.is_complete === true;
  const administeredStatus = ['given', 'self_administered'].includes(status);

  const handleSave = async () => {
    if (administeredStatus && med?.is_controlled_drug && !cdReady) {
      toast.error('Complete the two-witness Controlled Drug entry before recording this dose as administered');
      return;
    }
    if (['refused', 'error'].includes(status) && !reason.trim()) {
      toast.error('Please state the reason');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/service-users/${serviceUserId}/mar-entries`,
        {
          medication_id: med.id,
          administration_date: med.administration_date,
          scheduled_time: med.scheduled_time,
          status,
          actual_time: actualTime,
          dose_given: administeredStatus ? doseGiven : null,
          reason_detail: reason,
          shift_id: shiftId,
          cd_entry_id: administeredStatus && med.is_controlled_drug ? cdEntry?.id : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(status === 'given' ? `${med.medication_name} — recorded as given ✓` : 'Recorded');
      onSaved();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const allActions = [
    ...QUICK_ACTIONS,
    ...OTHER_STATUSES.map(s => ({ ...s, bg: 'bg-white', hover: 'hover:bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' })),
  ];

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={onClose}>
      <div
        className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Drag handle (mobile) */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-slate-200" />
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-[#0F5C5E] to-[#163A70] px-5 py-4 sm:rounded-t-2xl text-white">
          <p className="text-xs text-white/70 uppercase tracking-wider mb-0.5">Record Administration</p>
          <h2 className="text-lg font-bold leading-tight">{med.medication_name}</h2>
          <p className="text-sm text-white/80">{med.dose} · {med.form} · {fmtTime(med.scheduled_time)}</p>
          {med.administration_date && <p className="text-xs text-white/60 mt-0.5">{med.administration_date}</p>}
          {med.indication && <p className="text-xs text-white/60 mt-0.5">For: {med.indication}</p>}
        </div>

        <div className="p-5 space-y-4">
          {/* Covert alert */}
          {med.covert && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 font-medium">Covert administration in place — MCA best interests decision on file. Administer as directed.</p>
            </div>
          )}
          {med.special_instructions && (
            <div className="flex items-start gap-2 p-3 bg-[#D9F1F4]/30 border border-[#0F5C5E]/20 rounded-xl">
              <Info className="h-4 w-4 text-[#0F5C5E] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[#17324D]">{med.special_instructions}</p>
            </div>
          )}
          {med.is_controlled_drug && (
            <div className={`flex items-start gap-2 p-3 rounded-xl border ${cdReady ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
              <Shield className={`h-4 w-4 flex-shrink-0 mt-0.5 ${cdReady ? 'text-green-700' : 'text-[#163A70]'}`} />
              <p className={`text-xs ${cdReady ? 'text-green-800' : 'text-blue-900'}`}>
                {cdReady
                  ? 'Two-witness Controlled Drug entry complete for this exact scheduled dose. You can now record the MAR administration.'
                  : 'Given / self-administered is locked until the two-witness Controlled Drug entry is complete. You can still record a genuine non-administration status such as refusal or out of stock.'}
              </p>
            </div>
          )}

          {/* Quick action buttons */}
          <div className="grid grid-cols-3 gap-2">
            {QUICK_ACTIONS.map(a => {
              const Icon = a.icon;
              const selected = status === a.status;
              const blockedCdAdministration = med.is_controlled_drug && !cdReady && a.status === 'given';
              return (
                <button
                  key={a.status}
                  onClick={() => !blockedCdAdministration && setStatus(a.status)}
                  disabled={blockedCdAdministration}
                  className={`flex flex-col items-center gap-1.5 py-4 px-2 rounded-2xl border-2 transition-all
                    ${blockedCdAdministration
                      ? 'bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed'
                      : selected
                      ? `${a.bg} ${a.text} border-transparent shadow-md scale-105`
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                >
                  <Icon className={`h-6 w-6 ${selected ? 'text-white' : ''}`} />
                  <span className="text-xs font-semibold leading-tight text-center">{a.label}</span>
                </button>
              );
            })}
          </div>

          {/* Other statuses */}
          <div className="flex flex-wrap gap-2">
            {OTHER_STATUSES.map(a => {
              const Icon = a.icon;
              const selected = status === a.status;
              const blockedCdAdministration = med.is_controlled_drug && !cdReady && a.status === 'self_administered';
              return (
                <button
                  key={a.status}
                  onClick={() => !blockedCdAdministration && setStatus(a.status)}
                  disabled={blockedCdAdministration}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium transition-all
                    ${blockedCdAdministration
                      ? 'bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed'
                      : selected
                      ? 'bg-[#0F5C5E] text-white border-[#0F5C5E]'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                    }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {a.label}
                </button>
              );
            })}
          </div>

          {/* Actual time */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">Time administered</label>
            <input
              type="time"
              value={actualTime}
              onChange={e => setActualTime(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:ring-2 focus:ring-[#0F5C5E] focus:border-[#0F5C5E]"
            />
          </div>

          {/* Dose given */}
          {status === 'given' || status === 'self_administered' ? (
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Dose given</label>
              <input
                type="text"
                value={doseGiven}
                onChange={e => setDoseGiven(e.target.value)}
                placeholder={med.dose}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:ring-2 focus:ring-[#0F5C5E] focus:border-[#0F5C5E]"
              />
            </div>
          ) : (
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                {status === 'refused' ? 'Reason for refusal *' : 'Notes'}
              </label>
              <textarea
                rows={2}
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder={
                  status === 'refused'
                    ? 'State the reason the service user refused...'
                    : 'Any relevant notes...'
                }
                className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm resize-none focus:ring-2 focus:ring-[#0F5C5E] focus:border-[#0F5C5E]"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-6 pt-2 flex gap-3 border-t border-slate-100">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl h-12">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || (status === 'refused' && !reason.trim()) || (administeredStatus && med.is_controlled_drug && !cdReady)}
            className="flex-1 bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white rounded-xl h-12 text-sm font-semibold"
          >
            {saving ? 'Saving…' : 'Confirm & Sign'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ControlledDrugDialog({ med, serviceUserId, shiftId, serviceUserName, onClose, onSaved, onNonAdministration }) {
  const { user } = useAuth();
  const [quantity, setQuantity] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const entry = med.controlled_drug_entry;
  const userId = user?.user_id || user?.id;
  const isFirst = entry && entry.witness_1_id === userId;
  const sameWorker = entry?.witness_1_employee_id && entry.witness_1_employee_id === user?.employee_id;
  const witnessed = entry?.is_complete === true && Boolean(entry?.witness_2_id);
  const token = localStorage.getItem('token') || localStorage.getItem('adminToken');

  const submit = async () => {
    if (saving || !confirmed) return;
    setSaving(true);
    try {
      let response;
      const config = { headers: { Authorization: `Bearer ${token}` }, timeout: 18000 };
      if (!entry) {
        if (!Number.isInteger(Number(quantity)) || Number(quantity) <= 0) throw new Error('Enter the whole stock quantity used for this prescribed dose');
        response = await axios.post(`${API}/service-users/${serviceUserId}/cd-entries`, {
          medication_id: med.id, entry_type: 'administered', quantity: Number(quantity),
          administration_at: new Date().toISOString(), administration_date: med.administration_date,
          scheduled_time: med.scheduled_time, shift_id: shiftId, dose_given: med.dose,
        }, config);
        if (!response.data?.id || response.data.is_complete !== false) throw new Error('Could not verify the prepared event. Refresh before doing anything else.');
        toast.message('First record saved — awaiting a different worker’s witness. MAR is still unrecorded.');
      } else if (!witnessed && !isFirst && !sameWorker) {
        response = await axios.post(`${API}/service-users/${serviceUserId}/cd-entries/${entry.id}/witness`, { confirmed: true }, config);
        if (response.data?.id !== entry.id || response.data?.is_complete !== true || !response.data?.witness_2_id) throw new Error('Witness completion could not be verified. Refresh the visit.');
        toast.message('Witness saved — the first worker must now finalise this exact MAR dose.');
      } else if (witnessed && isFirst) {
        response = await axios.post(`${API}/service-users/${serviceUserId}/mar-entries`, {
          medication_id: med.id, administration_date: entry.administration_date, scheduled_time: entry.scheduled_time,
          actual_time: entry.actual_time, dose_given: entry.dose_given, status: 'given',
          shift_id: entry.shift_id, cd_entry_id: entry.id,
        }, config);
        if (response.data?.cd_entry_id !== entry.id || response.data?.status !== 'given') throw new Error('MAR completion could not be verified. Refresh the visit.');
        toast.success('Witnessed dose recorded on MAR');
      } else {
        return;
      }
      await onSaved();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || err.message || 'Could not save controlled-drug record');
      // Never retry automatically or advance local witness/administered state.
      await onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };
  const canAct = !entry || (!witnessed && !isFirst && !sameWorker) || (witnessed && isFirst);
  return (
    <div className="fixed inset-0 z-[86] flex items-end sm:items-center justify-center bg-black/50 sm:p-4" role="dialog" aria-modal="true" aria-label="Controlled drug administration">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl p-5 space-y-4 max-h-[92vh] overflow-y-auto">
        <div>
          <p className="text-xs uppercase tracking-wider text-blue-800">Controlled drug · exact scheduled dose</p>
          <h2 className="text-lg font-bold">{med.medication_name}</h2>
          <p className="text-sm font-semibold">{serviceUserName}</p>
          <p className="text-sm text-slate-600">{med.dose} · {med.form} · {med.route}</p>
          <p className="text-sm font-semibold">{med.administration_date} · {fmtTime(med.scheduled_time)}</p>
          {med.special_instructions && <p className="text-xs text-slate-600 mt-2">{med.special_instructions}</p>}
        </div>
        {!entry ? (
          <>
            <p className="text-sm text-blue-900">Only record a dose actually administered as prescribed. A different authorised worker must independently witness it from their own signed-in, clocked-in visit. This first record does not complete MAR.</p>
            <label className="block text-sm font-semibold">Stock quantity used (whole register units)
              <input type="number" min="1" step="1" value={quantity} onChange={e => setQuantity(e.target.value)} className="mt-1 w-full rounded-xl border p-3" />
            </label>
            <p className="text-xs text-slate-600">Use the quantity counted in the CD register, such as tablets. Do not enter the dose in mg as a stock count. Ask your manager if the stock unit is unclear.</p>
          </>
        ) : (
          <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 text-sm space-y-1">
            <p>First worker: {entry.witness_1_name}</p>
            <p>Dose: {entry.dose_given} · actual time: {entry.administration_at ? new Date(entry.administration_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' }) : entry.actual_time}</p>
            <p>Stock used: {entry.quantity} · balance: {entry.balance_before} → {entry.balance_after}</p>
            <p className="font-semibold">{witnessed ? 'Witnessed — awaiting first worker’s MAR record' : 'Awaiting independent second witness'}</p>
            {entry.witness_2_name && <p>Second witness: {entry.witness_2_name}</p>}
          </div>
        )}
        {!canAct && <p className="text-sm text-amber-900">{witnessed ? 'The first worker must finalise their own exact dose. Refresh after they save.' : 'You cannot witness your own record. The second worker opens this medication in their own active visit.'}</p>}
        {canAct && (
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} className="mt-1" />
            <span>{!entry ? 'I administered this exact prescribed dose and checked the stock quantity.' : witnessed ? 'I confirm this is my administration and these witnessed details are unchanged.' : 'I independently witnessed this administration and checked the same person, medicine, date, scheduled dose, actual dose/time and stock balance.'}</span>
          </label>
        )}
        <div className="flex gap-2">
          <Button variant="outline" disabled={saving} onClick={onClose}>Close</Button>
          {canAct && <Button className="flex-1 bg-[#163A70] text-white" disabled={saving || !confirmed || (!entry && (!quantity || !Number.isInteger(Number(quantity)) || Number(quantity) <= 0))} onClick={submit}>{saving ? 'Saving…' : !entry ? 'Record first worker' : witnessed ? 'Finalise exact MAR dose' : 'Confirm second witness'}</Button>}
        </div>
        {!entry && <Button variant="outline" disabled={saving} onClick={onNonAdministration} className="w-full">Dose was not administered</Button>}
      </div>
    </div>
  );
}

// ─── MEDICATION ROW ──────────────────────────────────────────────────────────
function MedicationRow({ med, onRecord, canRecord }) {
  const recorded = !!med.todays_entry;
  const entry = med.todays_entry;

  const statusLabel = {
    given: { label: 'Given', bg: 'bg-[#D8E6D5] text-[#1F8F5F]', icon: CheckCircle2 },
    refused: { label: 'Refused', bg: 'bg-red-100 text-red-700', icon: XCircle },
    not_required: { label: 'Not required', bg: 'bg-slate-100 text-slate-600', icon: MinusCircle },
    self_administered: { label: 'Self-admin', bg: 'bg-[#D8E6D5] text-[#1F8F5F]', icon: Shield },
    away: { label: 'Away', bg: 'bg-blue-100 text-blue-700', icon: Plane },
    hospital: { label: 'Hospital', bg: 'bg-blue-100 text-blue-700', icon: Building2 },
  }[entry?.status] || { label: 'Pending', bg: 'bg-amber-100 text-amber-700', icon: Clock };

  const StatusIcon = statusLabel.icon;

  return (
    <div
      className={`flex items-center gap-3 p-4 rounded-2xl border transition-all
        ${recorded
          ? 'bg-slate-50 border-slate-200'
          : 'bg-white border-slate-200 shadow-sm hover:border-[#0F5C5E]/30 hover:shadow-md'
        }`}
    >
      {/* Medication icon */}
      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0
        ${med.is_controlled_drug ? 'bg-[#163A70]/10' : recorded ? 'bg-slate-100' : 'bg-[#D9F1F4]/60'}`}>
        {med.is_controlled_drug
          ? <Shield className="h-5 w-5 text-[#163A70]" />
          : <Pill className={`h-5 w-5 ${recorded ? 'text-slate-400' : 'text-[#0F5C5E]'}`} />}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-semibold ${recorded ? 'text-slate-500' : 'text-slate-900'}`}>
            {med.medication_name}
          </span>
          {med.is_controlled_drug && (
            <Badge className="bg-[#163A70]/10 text-[#163A70] text-[10px] px-1.5 py-0 h-4">CD</Badge>
          )}
          {med.covert && (
            <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0 h-4">Covert</Badge>
          )}
        </div>
        <p className="text-xs text-slate-500">{med.dose} · {med.form} · {fmtTime(med.scheduled_time)}</p>
        {med.special_instructions && !recorded && (
          <p className="text-[11px] text-[#0F5C5E] mt-0.5 flex items-center gap-1">
            <Info className="h-3 w-3 flex-shrink-0" />{med.special_instructions}
          </p>
        )}
        {recorded && (
          <p className="text-[11px] text-slate-400 mt-0.5">Recorded by {entry.carer_name} at {fmtTime(entry.actual_time || entry.scheduled_time)}</p>
        )}
      </div>

      {/* Action / status badge */}
      {recorded ? (
        <span className={`flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs font-semibold ${statusLabel.bg}`}>
          <StatusIcon className="h-3.5 w-3.5" />
          {statusLabel.label}
        </span>
      ) : (
        <button
          onClick={() => canRecord && onRecord(med)}
          disabled={!canRecord}
          className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all flex-shrink-0
            ${canRecord
              ? 'bg-[#0F5C5E] text-white hover:bg-[#0c4a4c] active:scale-95 shadow-sm'
              : 'bg-slate-100 text-slate-400 cursor-not-allowed'
            }`}
        >
          Record
        </button>
      )}
    </div>
  );
}

// ─── PRN DIALOGS ─────────────────────────────────────────────────────────────
function PrnRecordDialog({ med, serviceUserId, shiftId, onClose, onSaved }) {
  const [indication, setIndication] = useState('');
  const [doseGiven, setDoseGiven] = useState(med?.dose || '');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  if (!med) return null;
  const token = localStorage.getItem('token') || localStorage.getItem('adminToken');

  const save = async () => {
    if (!indication.trim() || !doseGiven.trim()) {
      toast.error('State why the PRN is needed and the dose given');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/service-users/${serviceUserId}/prn-entries`,
        {
          medication_id: med.id,
          administered_at: new Date().toISOString(),
          dose_given: doseGiven,
          indication,
          notes,
          shift_id: shiftId,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('PRN administration recorded — remember the effectiveness follow-up');
      onSaved();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Could not record PRN');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center bg-black/50 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div>
          <p className="text-xs uppercase tracking-wider text-amber-700">PRN administration</p>
          <h2 className="text-lg font-bold text-slate-900">{med.medication_name}</h2>
          <p className="text-sm text-slate-600">{med.dose} · {med.form}</p>
          <p className="text-xs text-amber-800 mt-1">
            Min {med.prn_min_interval_minutes || '—'} min · max {med.prn_max_administrations_24h || '—'}/24h · review after {med.prn_review_after_minutes || '—'} min
          </p>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700">Why is it needed? *</label>
          <textarea rows={3} className="mt-1 w-full rounded-xl border border-slate-300 p-3 text-sm" value={indication} onChange={e => setIndication(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700">Dose given *</label>
          <input className="mt-1 w-full rounded-xl border border-slate-300 p-3 text-sm" value={doseGiven} onChange={e => setDoseGiven(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700">Note</label>
          <textarea rows={2} className="mt-1 w-full rounded-xl border border-slate-300 p-3 text-sm" value={notes} onChange={e => setNotes(e.target.value)} />
        </div>
        <p className="text-xs text-slate-500">CareOps checks your accepted shift, clock-in, PRN interval and rolling 24-hour limit before saving.</p>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1 bg-amber-700 text-white hover:bg-amber-800" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Record PRN'}</Button>
        </div>
      </div>
    </div>
  );
}

function PrnOutcomeDialog({ entry, serviceUserId, shiftId, onClose, onSaved }) {
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  if (!entry) return null;
  const token = localStorage.getItem('token') || localStorage.getItem('adminToken');

  const save = async () => {
    if (!outcome) {
      toast.error('Select the effectiveness outcome');
      return;
    }
    if (['partially_effective', 'no_effect'].includes(outcome) && !notes.trim()) {
      toast.error('Add follow-up notes');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/service-users/${serviceUserId}/prn-entries/${entry.id}/outcome`,
        { outcome, observed_at: new Date().toISOString(), notes, shift_id: shiftId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('PRN outcome recorded');
      onSaved();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Could not record outcome');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[86] flex items-end sm:items-center justify-center bg-black/50 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div>
          <p className="text-xs uppercase tracking-wider text-[#0F5C5E]">PRN effectiveness check</p>
          <h2 className="text-lg font-bold text-slate-900">{entry.medication_name}</h2>
          <p className="text-xs text-slate-600 mt-1">Given for: {entry.indication}</p>
        </div>
        <select className="w-full rounded-xl border border-slate-300 p-3 text-sm" value={outcome} onChange={e => setOutcome(e.target.value)}>
          <option value="">Select outcome</option>
          <option value="effective">Effective</option>
          <option value="partially_effective">Partially effective</option>
          <option value="no_effect">No effect</option>
        </select>
        <textarea rows={3} className="w-full rounded-xl border border-slate-300 p-3 text-sm" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Follow-up observation / action taken" />
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1 bg-[#0F5C5E] text-white" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save outcome'}</Button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function ShiftMedicationTask({ shift, onBack }) {
  const { user } = useAuth();
  const serviceUserId = shift?.shift?.service_user_id || shift?.service_user_id;
  const shiftId = shift?.shift?.id || shift?.id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMed, setSelectedMed] = useState(null);
  const [selectedPrn, setSelectedPrn] = useState(null);
  const [selectedPrnOutcome, setSelectedPrnOutcome] = useState(null);
  const [hasCompetency, setHasCompetency] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [cdNonAdministration, setCdNonAdministration] = useState(false);

  const token = localStorage.getItem('token') || localStorage.getItem('adminToken');

  const load = useCallback(async () => {
    if (!serviceUserId || !shiftId) return;
    setLoading(true);
    setLoadError(false);
    try {
      const res = await axios.get(
        `${API}/worker/shifts/${shiftId}/medication-task`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(res.data);
      setHasCompetency(res.data.has_competency || false);
    } catch (err) {
      setData(null);
      setHasCompetency(false);
      setSelectedMed(null);
      setSelectedPrn(null);
      setSelectedPrnOutcome(null);
      setLoadError(true);
      toast.error('Could not refresh this medication task. Recording is locked until a fresh load succeeds.');
    } finally {
      setLoading(false);
    }
  }, [serviceUserId, shiftId, token]);

  useEffect(() => { load(); }, [load]);

  const pending   = (data?.medications || []).filter(m => !m.todays_entry);
  const completed = (data?.medications || []).filter(m => !!m.todays_entry);
  const canRecord = !loading && !loadError && (hasCompetency || ['admin', 'super_admin', 'branch_manager'].includes(user?.role));
  const currentSelectedMed = (data?.medications || []).find(m => m.slot_id === selectedMed?.slot_id);

  const scheduleComplete = data?.schedule_calculation_complete === true;
  const allDone = scheduleComplete && pending.length === 0 && completed.length > 0;

  return (
    <div className="min-h-screen bg-[#F8FAFA] pb-28">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0F5C5E] to-[#163A70] text-white px-4 pt-4 pb-6">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={onBack} className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 transition-colors">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex-1">
            <p className="text-xs text-white/70 uppercase tracking-wider">Shift Task</p>
            <h1 className="text-lg font-bold leading-tight">Medication Administration</h1>
          </div>
          <button aria-label="Refresh medication task" onClick={load} className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 transition-colors">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {data?.service_user_name && (
          <p className="text-sm text-white/80 ml-11">{data.service_user_name}</p>
        )}
        {/* Progress bar */}
        {!loading && scheduleComplete && data?.medications?.length > 0 && (
          <div className="mt-3 ml-11">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-white/70">{completed.length} of {data.medications.length} recorded</span>
              {allDone && <span className="text-xs text-white font-semibold flex items-center gap-1"><CheckCheck className="h-3.5 w-3.5" />All done</span>}
            </div>
            <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all duration-500"
                style={{ width: `${data.medications.length > 0 ? (completed.length / data.medications.length) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="px-4 -mt-3 space-y-4">
        {/* Allergy banner */}
        {data?.allergies?.length > 0 && (
          <div className="bg-red-600 text-white rounded-2xl p-4 shadow-lg">
            <div className="flex items-center gap-2 mb-1.5">
              <AlertTriangle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-bold uppercase tracking-wide">Known Allergies</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {data.allergies.map((a, i) => (
                <span key={i} className="bg-white/20 px-3 py-1 rounded-lg text-sm font-medium">{a}</span>
              ))}
            </div>
          </div>
        )}

        {!loading && data && !scheduleComplete && (
          <div role="alert" className="rounded-2xl border border-amber-300 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-amber-900">Schedule review required</p>
            <p className="mt-1 text-xs text-amber-800">The full medication schedule cannot be verified. Do not guess doses or times. Contact your manager; known scheduled doses remain listed below.</p>
            <ul className="mt-2 space-y-1 text-xs text-amber-900">
              {(data.schedule_issues || []).map(issue => <li key={issue.medication_id}>{issue.medication_name}: {issue.reason}</li>)}
            </ul>
          </div>
        )}
        {/* Competency lock */}
        {!loading && !canRecord && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
            <Lock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">Medication competency required</p>
              <p className="text-xs text-amber-700 mt-0.5">You can view this schedule but cannot record administrations. Please speak to your manager about completing the medication competency assessment.</p>
            </div>
          </div>
        )}

        {loadError && <div role="alert" className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-800">Medication task unavailable. Refresh to get current records before administering or witnessing.</div>}
        {/* Loading */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-8 h-8 rounded-full border-2 border-[#0F5C5E] border-t-transparent animate-spin" />
            <p className="text-xs text-slate-400">Loading medication schedule…</p>
          </div>
        ) : scheduleComplete && data?.medications?.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Pill className="h-8 w-8 text-slate-300" />
            </div>
            <p className="text-sm font-semibold text-slate-700 mb-1">No medications scheduled</p>
            <p className="text-xs text-slate-500">No medications are due for this service user during your shift window, or none have been added yet.</p>
          </div>
        ) : (
          <>
            {/* All done banner */}
            {allDone && (
              <div className="bg-[#D8E6D5] border border-[#1F8F5F]/20 rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#1F8F5F] flex items-center justify-center flex-shrink-0">
                  <CheckCheck className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1F8F5F]">All medications recorded</p>
                  <p className="text-xs text-[#1F8F5F]/80">Each scheduled dose has a MAR record. Refusal and other non-administration statuses remain separate from doses given.</p>
                </div>
              </div>
            )}

            {/* Pending */}
            {pending.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-1">
                  To administer · {pending.length} remaining
                </p>
                {pending.map(med => (
                  <MedicationRow
                    key={med.slot_id || med.id}
                    med={med}
                    canRecord={canRecord}
                    onRecord={med => { setCdNonAdministration(false); setSelectedMed(med); }}
                  />
                ))}
              </div>
            )}

            {/* Completed */}
            {completed.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-1">
                  Recorded · {completed.length}
                </p>
                {completed.map(med => (
                  <MedicationRow
                    key={med.slot_id || med.id}
                    med={med}
                    canRecord={canRecord}
                    onRecord={med => { setCdNonAdministration(false); setSelectedMed(med); }}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* PRN — stays inside the active visit and uses the same backend safety gate. */}
        {!loading && data?.prn_medications?.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />PRN (as-required) medications
            </p>
            <div className="space-y-2">
              {data.prn_medications.map(med => {
                const protocolComplete = Boolean(
                  med.indication && med.special_instructions
                  && med.max_prn_dose_24h && med.prn_min_interval_minutes
                  && med.prn_max_administrations_24h && med.prn_review_after_minutes
                );
                return (
                  <div key={med.id} className="rounded-xl border border-amber-200 bg-white/70 p-3 flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold text-amber-900">{med.medication_name} {med.dose}</p>
                      <p className="text-[11px] text-amber-700 mt-0.5">
                        {protocolComplete
                          ? `Min ${med.prn_min_interval_minutes} min · max ${med.prn_max_administrations_24h}/24h · review after ${med.prn_review_after_minutes} min`
                          : 'PRN protocol incomplete — administration blocked'}
                      </p>
                    </div>
                    {med.is_controlled_drug && <p className="text-xs text-blue-900">Controlled PRN is blocked pending its witnessed PRN workflow. Contact your manager; do not use scheduled MAR instead.</p>}
                    {canRecord && protocolComplete && !med.is_controlled_drug && (
                      <Button size="sm" variant="outline" onClick={() => setSelectedPrn(med)}>Give PRN</Button>
                    )}
                  </div>
                );
              })}
            </div>
            {(data.today_prn_entries || []).some(e => !e.outcome || e.outcome === 'not_assessed') && (
              <div className="mt-3 pt-3 border-t border-amber-200">
                <p className="text-xs font-semibold text-amber-900 mb-2">Effectiveness follow-up</p>
                <div className="space-y-2">
                  {(data.today_prn_entries || []).filter(e => !e.outcome || e.outcome === 'not_assessed').map(entry => {
                    const reviewReady = !entry.review_due_at || new Date(entry.review_due_at) <= new Date();
                    return (
                      <div key={entry.id} className="flex items-center justify-between gap-3 rounded-lg bg-white/70 p-2">
                        <div>
                          <p className="text-xs font-medium text-slate-800">{entry.medication_name}</p>
                          <p className="text-[10px] text-slate-500">{entry.review_due_at ? `Review due ${new Date(entry.review_due_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : 'Review effectiveness'}</p>
                        </div>
                        {canRecord && (reviewReady
                          ? <Button size="sm" variant="outline" onClick={() => setSelectedPrnOutcome(entry)}>Record outcome</Button>
                          : <span className="text-[10px] font-medium text-amber-700">Not due yet</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* CD notice */}
        {!loading && data?.medications?.some(m => m.is_controlled_drug) && (
          <div className="bg-[#163A70]/5 border border-[#163A70]/15 rounded-2xl p-4 flex items-start gap-2.5">
            <Shield className="h-4 w-4 text-[#163A70] flex-shrink-0 mt-0.5" />
            <p className="text-xs text-[#163A70]">
              <span className="font-semibold">Controlled drug(s) on this schedule.</span> Open the exact scheduled dose here. A different authorised worker witnesses from their own signed-in visit, then the first worker finalises MAR. The manager register uses the same event.
            </p>
          </div>
        )}
      </div>

      {/* Record dialog */}
      {canRecord && currentSelectedMed?.is_controlled_drug && !currentSelectedMed.todays_entry && !cdNonAdministration ? (
        <ControlledDrugDialog key={currentSelectedMed.slot_id} med={currentSelectedMed} serviceUserId={serviceUserId} shiftId={shiftId} serviceUserName={data?.service_user_name} onClose={() => setSelectedMed(null)} onSaved={load} onNonAdministration={() => setCdNonAdministration(true)} />
      ) : <RecordDialog
        open={canRecord && !!currentSelectedMed && !currentSelectedMed.todays_entry}
        med={currentSelectedMed}
        onClose={() => setSelectedMed(null)}
        onSaved={load}
        serviceUserId={serviceUserId}
        shiftId={shiftId}
      />}
      <PrnRecordDialog
        med={selectedPrn}
        serviceUserId={serviceUserId}
        shiftId={shiftId}
        onClose={() => setSelectedPrn(null)}
        onSaved={load}
      />
      <PrnOutcomeDialog
        entry={selectedPrnOutcome}
        shiftId={shiftId}
        serviceUserId={serviceUserId}
        onClose={() => setSelectedPrnOutcome(null)}
        onSaved={load}
      />
    </div>
  );
}
